import { useCallback, useMemo, useState } from "react";
import styled, { css } from "styled-components";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { useIsTallerThanViewport } from "src/utils/hooks/useIsTallerThanViewport";
import { spacing } from "src/design-system/tokens/spacing";
import { useFeature } from "src/feature/useFeature";
import { getHeaderHeightPadded } from "../GlobalHeader/GlobalHeader";
import { RefreshingAd } from "../RefreshingAd/RefreshingAd";
import { DisplayAd } from "../DisplayAd/DisplayAd";

type AdStatus = "rendered" | "failed";
type AdSlotStatuses = {
  adSlot1?: AdStatus;
  adSlot2?: AdStatus;
};

export function RightRail() {
  const { isXlScreen } = useScreenMinWidth();
  const [adSlotsStatus, setAdSlotsStatus] = useState<AdSlotStatuses>({
    adSlot1: undefined,
    adSlot2: undefined,
  });
  const refreshAdUnits = useFeature("RefreshAdUnits");

  // Used to update right rail visibility based on whether ads
  // are still loading, have filled or failed to fill.
  const rightRailStatus = useMemo(() => {
    if (
      adSlotsStatus.adSlot1 === "rendered" ||
      adSlotsStatus.adSlot2 === "rendered"
    ) {
      return "hasAd";
    }
    if (
      adSlotsStatus.adSlot1 === "failed" &&
      adSlotsStatus.adSlot2 === "failed"
    ) {
      return "adsFailedToFill";
    }
    return "loadingAd";
  }, [adSlotsStatus]);

  const handleAdUpdate = useCallback(
    (adSlot: keyof AdSlotStatuses, status: AdStatus) => {
      setAdSlotsStatus((prev) => ({
        ...prev,
        [adSlot]: status,
      }));
    },
    [],
  );

  const { contentRef, isTallerThanViewport } = useIsTallerThanViewport({
    isDisabled: rightRailStatus !== "hasAd",
    offsetTop: getHeaderHeightPadded(),
  });

  // these values come from apps\explore\src\components\DisplayAd\config.ts.
  // we only want to calculate the width once as the ad will never resize.
  const [rightRailWidth] = useState(isXlScreen ? 300 : 160);

  const AdComponent = refreshAdUnits ? RefreshingAd : DisplayAd;

  return (
    <Container
      ref={contentRef}
      $isTallerThanViewport={isTallerThanViewport}
      $width={rightRailStatus !== "adsFailedToFill" ? rightRailWidth : 0}
    >
      <RefreshingAd
        slotConfigId="rightRail"
        onFilled={() => handleAdUpdate("adSlot1", "rendered")}
        onNotFilled={() => handleAdUpdate("adSlot1", "failed")}
        variant="TieredDisplayAd"
        adHeight={600}
      />
      <AdComponent
        slotConfigId="rightRail2"
        onFilled={() => handleAdUpdate("adSlot2", "rendered")}
        onNotFilled={() => handleAdUpdate("adSlot2", "failed")}
        variant="DisplayAd"
        adHeight={600}
      />
    </Container>
  );
}

const AdsGridStickyStyles = css<{
  $isTallerThanViewport: boolean;
  $width: number;
}>`
  position: sticky;
  ${({ $width }) => `
    width: ${$width}px;
    margin-left: ${$width ? spacing.xl : 0};
  `}
  transition: width 0.3s ease-in-out;

  ${(props) =>
    props.$isTallerThanViewport
      ? css`
          align-self: flex-end;
          top: auto;
          bottom: 0;
        `
      : css`
          align-self: flex-start;
          top: var(--spacing-header-height-padded);
          bottom: auto;
        `}
`;

const Container = styled.div<{
  $isTallerThanViewport: boolean;
  $width: number;
}>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  ${AdsGridStickyStyles}
`;
